import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { mediaService } from '../../lib/services';
import Icon from '../../components/AppIcon';
import Header from '../../components/ui/Header';
import Sidebar from '../../components/ui/Sidebar';

const ACCEPTED_TYPES = 'image/jpeg,image/jpg,image/png,image/gif,image/webp,image/svg+xml,video/mp4,video/webm,application/pdf,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document';

const formatBytes = (bytes) => {
  if (!bytes) return '0 B';
  const k = 1024;
  const sizes = ['B', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return `${parseFloat((bytes / Math.pow(k, i))?.toFixed(1))} ${sizes?.[i]}`;
};

const getMediaIcon = (mimeType) => {
  if (!mimeType) return 'File';
  if (mimeType?.startsWith('image/')) return 'Image';
  if (mimeType?.startsWith('video/')) return 'Video';
  if (mimeType?.includes('pdf')) return 'FileText';
  return 'File';
};

const MediaCard = ({ item, onDelete, onSelect, selectable }) => {
  const isImage = item?.mimeType?.startsWith('image/');
  const publicUrl = mediaService?.getPublicUrl(item?.filePath);

  return (
    <div
      className={`bg-card border border-border rounded-lg overflow-hidden group transition-all hover:border-primary/50 hover:shadow-md ${selectable ? 'cursor-pointer' : ''}`}
      onClick={selectable ? () => onSelect?.(item, publicUrl) : undefined}
    >
      <div className="relative aspect-square bg-muted flex items-center justify-center overflow-hidden">
        {isImage && publicUrl ? (
          <img
            src={publicUrl}
            alt={item?.altText || item?.originalName}
            className="w-full h-full object-cover"
            onError={(e) => { e.target.style.display = 'none'; e.target.nextSibling.style.display = 'flex'; }}
          />
        ) : null}
        <div className={`${isImage && publicUrl ? 'hidden' : 'flex'} w-full h-full items-center justify-center`}>
          <Icon name={getMediaIcon(item?.mimeType)} size={40} className="text-muted-foreground" />
        </div>
        {!selectable && onDelete && (
          <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center gap-2">
            <button
              onClick={(e) => { e?.stopPropagation(); window.open(publicUrl, '_blank'); }}
              className="p-2 bg-white/20 hover:bg-white/30 rounded-lg text-white transition-colors"
              title="View file"
            >
              <Icon name="ExternalLink" size={16} />
            </button>
            <button
              onClick={(e) => { e?.stopPropagation(); onDelete?.(item); }}
              className="p-2 bg-red-500/80 hover:bg-red-600/80 rounded-lg text-white transition-colors"
              title="Delete file"
            >
              <Icon name="Trash2" size={16} />
            </button>
          </div>
        )}
      </div>
      <div className="p-3">
        <p className="text-sm font-medium text-foreground truncate" title={item?.originalName}>{item?.originalName}</p>
        <div className="flex items-center justify-between mt-1">
          <span className="text-xs text-muted-foreground">{formatBytes(item?.fileSize)}</span>
          <span className="text-xs text-muted-foreground capitalize">{item?.mediaType}</span>
        </div>
      </div>
    </div>
  );
};

const UploadZone = ({ onUpload, uploading }) => {
  const fileInputRef = useRef(null);
  const [isDragging, setIsDragging] = useState(false);

  const handleDrop = useCallback((e) => {
    e?.preventDefault();
    setIsDragging(false);
    const files = Array.from(e?.dataTransfer?.files || []);
    if (files?.length > 0) onUpload(files);
  }, [onUpload]);

  const handleDragOver = (e) => { e?.preventDefault(); setIsDragging(true); };
  const handleDragLeave = () => setIsDragging(false);

  const handleFileChange = (e) => {
    const files = Array.from(e?.target?.files || []);
    if (files?.length > 0) onUpload(files);
    e.target.value = '';
  };

  return (
    <div
      onDrop={handleDrop}
      onDragOver={handleDragOver}
      onDragLeave={handleDragLeave}
      className={`border-2 border-dashed rounded-xl p-8 text-center transition-all ${isDragging ? 'border-primary bg-primary/5' : 'border-border hover:border-primary/50 hover:bg-muted/30'}`}
    >
      <input
        ref={fileInputRef}
        type="file"
        multiple
        accept={ACCEPTED_TYPES}
        onChange={handleFileChange}
        className="hidden"
      />
      {uploading ? (
        <div className="flex flex-col items-center gap-3">
          <div className="w-10 h-10 border-2 border-primary border-t-transparent rounded-full animate-spin" />
          <p className="text-sm text-muted-foreground">Uploading files...</p>
        </div>
      ) : (
        <div className="flex flex-col items-center gap-3">
          <div className="w-12 h-12 bg-primary/10 rounded-full flex items-center justify-center">
            <Icon name="Upload" size={24} className="text-primary" />
          </div>
          <div>
            <p className="text-sm font-medium text-foreground">Drop files here or</p>
            <button
              onClick={() => fileInputRef?.current?.click()}
              className="text-sm text-primary hover:underline font-medium"
            >
              browse to upload
            </button>
          </div>
          <p className="text-xs text-muted-foreground">Images, videos, PDFs, documents up to 50MB</p>
        </div>
      )}
    </div>
  );
};

const MediaLibrary = ({ selectable = false, onSelect, onClose }) => {
  const { isEditor, user } = useAuth();
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [mediaItems, setMediaItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState(null);
  const [uploadErrors, setUploadErrors] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [filterType, setFilterType] = useState('all');
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [viewMode, setViewMode] = useState('grid');

  const loadMedia = useCallback(async () => {
    setLoading(true);
    setError(null);
    const filters = {};
    if (filterType !== 'all') filters.mediaType = filterType;
    if (searchQuery) filters.search = searchQuery;
    const { data, error: fetchError } = await mediaService?.getAll(filters);
    if (fetchError) setError(fetchError?.message || 'Failed to load media');
    else setMediaItems(data || []);
    setLoading(false);
  }, [filterType, searchQuery]);

  useEffect(() => {
    loadMedia();
  }, [loadMedia]);

  const handleUpload = async (files) => {
    if (!isEditor) return;
    setUploading(true);
    setUploadErrors([]);
    const errors = [];

    for (const file of files) {
      const { data, error: uploadError } = await mediaService?.upload(file, 'uploads');
      if (uploadError) {
        errors?.push(`${file?.name}: ${uploadError?.message || 'Upload failed'}`);
      } else if (data) {
        setMediaItems(prev => [data, ...prev]);
      }
    }

    if (errors?.length > 0) setUploadErrors(errors);
    setUploading(false);
  };

  const handleDelete = async () => {
    if (!deleteConfirm) return;
    setDeleting(true);
    const { error: deleteError } = await mediaService?.delete(deleteConfirm?.id, deleteConfirm?.filePath);
    if (deleteError) {
      setError(deleteError?.message || 'Failed to delete file');
    } else {
      setMediaItems(prev => prev?.filter(m => m?.id !== deleteConfirm?.id));
    }
    setDeleting(false);
    setDeleteConfirm(null);
  };

  const filteredItems = mediaItems?.filter(item => {
    if (filterType !== 'all' && item?.mediaType !== filterType) return false;
    if (searchQuery && !item?.originalName?.toLowerCase()?.includes(searchQuery?.toLowerCase())) return false;
    return true;
  });

  const stats = {
    total: mediaItems?.length || 0,
    images: mediaItems?.filter(m => m?.mediaType === 'image')?.length || 0,
    videos: mediaItems?.filter(m => m?.mediaType === 'video')?.length || 0,
    documents: mediaItems?.filter(m => m?.mediaType === 'document')?.length || 0,
  };

  // Render as modal/picker when selectable
  if (selectable) {
    return (
      <div className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4">
        <div className="bg-card rounded-xl shadow-2xl w-full max-w-4xl max-h-[85vh] flex flex-col">
          <div className="flex items-center justify-between p-4 border-b border-border">
            <h2 className="text-lg font-semibold text-foreground">Select Media</h2>
            <button onClick={onClose} className="p-2 hover:bg-muted rounded-lg transition-colors">
              <Icon name="X" size={20} />
            </button>
          </div>
          <div className="p-4 border-b border-border">
            <UploadZone onUpload={handleUpload} uploading={uploading} />
          </div>
          <div className="flex items-center gap-3 px-4 py-3 border-b border-border">
            <div className="relative flex-1">
              <Icon name="Search" size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
              <input
                type="text"
                placeholder="Search media..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e?.target?.value)}
                className="w-full pl-9 pr-3 py-2 text-sm bg-muted border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary/30"
              />
            </div>
            <select
              value={filterType}
              onChange={(e) => setFilterType(e?.target?.value)}
              className="px-3 py-2 text-sm bg-muted border border-border rounded-lg focus:outline-none"
            >
              <option value="all">All Types</option>
              <option value="image">Images</option>
              <option value="video">Videos</option>
              <option value="document">Documents</option>
            </select>
          </div>
          <div className="flex-1 overflow-y-auto p-4">
            {loading ? (
              <div className="flex items-center justify-center h-40">
                <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin" />
              </div>
            ) : filteredItems?.length === 0 ? (
              <div className="flex flex-col items-center justify-center h-40 gap-2">
                <Icon name="Image" size={40} className="text-muted-foreground" />
                <p className="text-sm text-muted-foreground">No media found. Upload files above.</p>
              </div>
            ) : (
              <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-5 gap-3">
                {filteredItems?.map(item => (
                  <MediaCard key={item?.id} item={item} onSelect={onSelect} selectable={true} />
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    );
  }

  // Full page view
  return (
    <div className="app-layout">
      <Sidebar isCollapsed={sidebarCollapsed} onToggleCollapse={setSidebarCollapsed} />
      <div className={`main-content ${sidebarCollapsed ? 'collapsed' : ''}`}>
        <Header isCollapsed={sidebarCollapsed} userName={user?.fullName || 'User'} userRole={user?.role || 'viewer'} />
        <main className="content-area">
          {/* Page Header */}
          <div className="flex items-center justify-between mb-6">
            <div>
              <h1 className="text-2xl font-bold text-foreground">Media Library</h1>
              <p className="text-sm text-muted-foreground mt-1">Upload and manage your media files</p>
            </div>
            <div className="flex items-center gap-2">
              <button
                onClick={() => setViewMode(viewMode === 'grid' ? 'list' : 'grid')}
                className="p-2 border border-border rounded-lg hover:bg-muted transition-colors"
                title={viewMode === 'grid' ? 'List view' : 'Grid view'}
              >
                <Icon name={viewMode === 'grid' ? 'List' : 'LayoutGrid'} size={18} />
              </button>
            </div>
          </div>

          {/* Stats */}
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mb-6">
            {[
              { label: 'Total Files', value: stats?.total, icon: 'Files', color: 'text-primary' },
              { label: 'Images', value: stats?.images, icon: 'Image', color: 'text-blue-500' },
              { label: 'Videos', value: stats?.videos, icon: 'Video', color: 'text-purple-500' },
              { label: 'Documents', value: stats?.documents, icon: 'FileText', color: 'text-orange-500' },
            ]?.map(stat => (
              <div key={stat?.label} className="bg-card border border-border rounded-lg p-4 flex items-center gap-3">
                <div className={`p-2 bg-muted rounded-lg ${stat?.color}`}>
                  <Icon name={stat?.icon} size={20} />
                </div>
                <div>
                  <p className="text-xl font-bold text-foreground">{stat?.value}</p>
                  <p className="text-xs text-muted-foreground">{stat?.label}</p>
                </div>
              </div>
            ))}
          </div>

          {/* Upload Zone */}
          {isEditor && (
            <div className="mb-6">
              <UploadZone onUpload={handleUpload} uploading={uploading} />
              {uploadErrors?.length > 0 && (
                <div className="mt-3 p-3 bg-error/10 border border-error/20 rounded-lg">
                  {uploadErrors?.map((err, i) => (
                    <p key={i} className="text-sm text-error">{err}</p>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* Filters & Search */}
          <div className="flex flex-col sm:flex-row gap-3 mb-4">
            <div className="relative flex-1">
              <Icon name="Search" size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
              <input
                type="text"
                placeholder="Search by filename..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e?.target?.value)}
                className="w-full pl-9 pr-3 py-2 text-sm bg-card border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary/30"
              />
            </div>
            <div className="flex gap-2">
              {['all', 'image', 'video', 'document']?.map(type => (
                <button
                  key={type}
                  onClick={() => setFilterType(type)}
                  className={`px-3 py-2 text-sm rounded-lg border transition-colors capitalize ${filterType === type ? 'bg-primary text-primary-foreground border-primary' : 'bg-card border-border hover:bg-muted'}`}
                >
                  {type === 'all' ? 'All' : type + 's'}
                </button>
              ))}
            </div>
          </div>

          {/* Error State */}
          {error && (
            <div className="mb-4 p-3 bg-error/10 border border-error/20 rounded-lg flex items-center gap-2">
              <Icon name="AlertCircle" size={16} className="text-error" />
              <p className="text-sm text-error">{error}</p>
              <button onClick={() => setError(null)} className="ml-auto text-error hover:text-error/70">
                <Icon name="X" size={14} />
              </button>
            </div>
          )}

          {/* Media Grid / List */}
          {loading ? (
            <div className="flex items-center justify-center h-64">
              <div className="w-10 h-10 border-2 border-primary border-t-transparent rounded-full animate-spin" />
            </div>
          ) : filteredItems?.length === 0 ? (
            <div className="flex flex-col items-center justify-center h-64 gap-3 bg-card border border-border rounded-xl">
              <Icon name="Image" size={48} className="text-muted-foreground" />
              <p className="text-foreground font-medium">No media files found</p>
              <p className="text-sm text-muted-foreground">
                {searchQuery || filterType !== 'all' ? 'Try adjusting your filters' : 'Upload your first file above'}
              </p>
            </div>
          ) : viewMode === 'grid' ? (
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 gap-4">
              {filteredItems?.map(item => (
                <MediaCard key={item?.id} item={item} onDelete={setDeleteConfirm} selectable={false} />
              ))}
            </div>
          ) : (
            <div className="bg-card border border-border rounded-xl overflow-hidden">
              <table className="w-full">
                <thead className="bg-muted/50 border-b border-border">
                  <tr>
                    <th className="text-left px-4 py-3 text-xs font-medium text-muted-foreground uppercase">File</th>
                    <th className="text-left px-4 py-3 text-xs font-medium text-muted-foreground uppercase">Type</th>
                    <th className="text-left px-4 py-3 text-xs font-medium text-muted-foreground uppercase">Size</th>
                    <th className="text-left px-4 py-3 text-xs font-medium text-muted-foreground uppercase">Uploaded By</th>
                    <th className="text-right px-4 py-3 text-xs font-medium text-muted-foreground uppercase">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-border">
                  {filteredItems?.map(item => {
                    const publicUrl = mediaService?.getPublicUrl(item?.filePath);
                    const isImage = item?.mimeType?.startsWith('image/');
                    return (
                      <tr key={item?.id} className="hover:bg-muted/30 transition-colors">
                        <td className="px-4 py-3">
                          <div className="flex items-center gap-3">
                            <div className="w-10 h-10 bg-muted rounded-lg flex items-center justify-center overflow-hidden flex-shrink-0">
                              {isImage && publicUrl ? (
                                <img src={publicUrl} alt={item?.originalName} className="w-full h-full object-cover" />
                              ) : (
                                <Icon name={getMediaIcon(item?.mimeType)} size={18} className="text-muted-foreground" />
                              )}
                            </div>
                            <span className="text-sm text-foreground truncate max-w-[200px]">{item?.originalName}</span>
                          </div>
                        </td>
                        <td className="px-4 py-3">
                          <span className="text-xs px-2 py-1 bg-muted rounded-full capitalize">{item?.mediaType}</span>
                        </td>
                        <td className="px-4 py-3 text-sm text-muted-foreground">{formatBytes(item?.fileSize)}</td>
                        <td className="px-4 py-3 text-sm text-muted-foreground">{item?.uploader?.fullName || 'Unknown'}</td>
                        <td className="px-4 py-3">
                          <div className="flex items-center justify-end gap-2">
                            <button
                              onClick={() => window.open(publicUrl, '_blank')}
                              className="p-1.5 hover:bg-muted rounded-lg transition-colors text-muted-foreground hover:text-foreground"
                              title="View"
                            >
                              <Icon name="ExternalLink" size={14} />
                            </button>
                            <button
                              onClick={() => { navigator.clipboard?.writeText(publicUrl); }}
                              className="p-1.5 hover:bg-muted rounded-lg transition-colors text-muted-foreground hover:text-foreground"
                              title="Copy URL"
                            >
                              <Icon name="Copy" size={14} />
                            </button>
                            <button
                              onClick={() => setDeleteConfirm(item)}
                              className="p-1.5 hover:bg-error/10 rounded-lg transition-colors text-muted-foreground hover:text-error"
                              title="Delete"
                            >
                              <Icon name="Trash2" size={14} />
                            </button>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </main>
      </div>
      {/* Delete Confirmation Modal */}
      {deleteConfirm && (
        <div className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4">
          <div className="bg-card rounded-xl shadow-2xl w-full max-w-sm p-6">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 bg-error/10 rounded-full flex items-center justify-center">
                <Icon name="Trash2" size={20} className="text-error" />
              </div>
              <div>
                <h3 className="font-semibold text-foreground">Delete File</h3>
                <p className="text-sm text-muted-foreground">This action cannot be undone</p>
              </div>
            </div>
            <p className="text-sm text-foreground mb-6">
              Are you sure you want to delete <strong>{deleteConfirm?.originalName}</strong>?
            </p>
            <div className="flex gap-3">
              <button
                onClick={() => setDeleteConfirm(null)}
                disabled={deleting}
                className="flex-1 px-4 py-2 border border-border rounded-lg text-sm hover:bg-muted transition-colors disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                onClick={handleDelete}
                disabled={deleting}
                className="flex-1 px-4 py-2 bg-error text-white rounded-lg text-sm hover:bg-error/90 transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
              >
                {deleting ? <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" /> : null}
                Delete
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default MediaLibrary;
